import { FeatureFlags } from "@/constants/featureFlags";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { AnalyticsEventType } from "@/constants/analytics";
import { formatPrice } from "../../SummaryPrice";
import { getClusterHourCount } from "@/store/cluster";
import { getTotalCostSavingsFromComparison, useCompetitorPriceMap } from "../priceComparison";
import { ClusterType, SimpleClusterForm } from "@/types";
import { useEffect } from "react";
import { trackEvent } from "@/utils/analytics";

export const useTotalSavings = ({
  totalCost,
  initialTotalCost,
  values,
  clusterType,
  isPriceLoaded,
  isOpen
}: {
  totalCost: number | undefined;
  initialTotalCost: number | undefined;
  values: SimpleClusterForm;
  clusterType: ClusterType;
  isPriceLoaded: boolean;
  isOpen: boolean;
}) => {
  const priceComparisonVariant = useFeatureFlag(FeatureFlags.PRICE_COMPARISON);
  const isPriceComparisonEnabled =
    priceComparisonVariant && priceComparisonVariant.value !== "control" ? true : false;
  const competitorPriceMap = useCompetitorPriceMap();
  const totalCostNumber = Number(totalCost || initialTotalCost || 0);
  const durationQty = values.durationQuantity ?? 1;
  const durationType = values.durationType ?? (clusterType === "kubernetes" ? "daily" : "hourly");
  const selectedHours = getClusterHourCount(durationQty, durationType) ?? durationQty;
  const conversionRate = values.hardwareInfo?.conversionRate;
  const totalCostInUsd =
    values.currency === "iocoin" && conversionRate
      ? totalCostNumber / conversionRate
      : totalCostNumber;
  const currentPricePerHour =
    Number.isFinite(selectedHours) && selectedHours > 0 ? totalCostInUsd / selectedHours : 0;
  const { totalSavingsAmount } = getTotalCostSavingsFromComparison(
    {
      hardwareIdentifier: values.hardware || undefined,
      deploymentId: values.deploymentId || undefined,
      hardwareId: values.hardware || undefined
    },
    currentPricePerHour,
    totalCostInUsd,
    competitorPriceMap
  );
  const totalSavingsAmountFormatted = formatPrice("credits", totalSavingsAmount, 2);
  const showSavingsMessage = isPriceComparisonEnabled && isPriceLoaded && totalSavingsAmount > 0;

  useEffect(() => {
    if (isOpen && showSavingsMessage) {
      trackEvent(AnalyticsEventType.PAYMENT_SAVINGS_MESSAGE_SHOWN, {
        totalSavingsAmount: totalSavingsAmountFormatted,
        totalCost: totalCostInUsd
      });
    }
  }, [isOpen, showSavingsMessage, totalSavingsAmountFormatted, totalCostInUsd]);

  return {
    totalSavingsAmountFormatted,
    totalCostInUsd,
    showSavingsMessage
  };
};
